import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Eye,
  EyeOff,
  Save,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  Loader2,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Manages ROSS1000 "group account" credentials grouped by Region → Province.
 * Each row represents a province-level shared account used by multiple properties.
 */
const Ross1000GroupCredentialsManager = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // id being saved
  const [showPwd, setShowPwd] = useState({}); // { [id]: bool }
  const [expanded, setExpanded] = useState({}); // { [region]: bool }
  const [editDrafts, setEditDrafts] = useState({}); // { [id]: { username, password } }
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEntry, setNewEntry] = useState({
    region: '',
    province: '',
    province_code: '',
    username: '',
    password: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/api/ross1000/group-credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.credentials || [];
      setCredentials(list);
      // Auto-expand every region at first load
      const initial = {};
      list.forEach((c) => {
        initial[c.region] = true;
      });
      setExpanded(initial);
    } catch (err) {
      console.error('Error fetching group credentials', err);
      toast.error('Errore caricamento credenziali di gruppo');
    } finally {
      setLoading(false);
    }
  };

  const handleDraftChange = (id, field, value) => {
    setEditDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const getFieldValue = (cred, field) => {
    const draft = editDrafts[cred.id];
    if (draft && draft[field] !== undefined) return draft[field];
    return cred[field] || '';
  };

  const isDirty = (cred) => {
    const draft = editDrafts[cred.id];
    if (!draft) return false;
    return (
      (draft.username !== undefined && draft.username !== cred.username) ||
      (draft.password !== undefined && draft.password !== cred.password)
    );
  };

  const saveCredential = async (cred) => {
    try {
      setSaving(cred.id);
      const token = localStorage.getItem('hw_token');
      const payload = {
        region: cred.region,
        province: cred.province,
        province_code: cred.province_code,
        username: getFieldValue(cred, 'username'),
        password: getFieldValue(cred, 'password'),
      };
      await axios.put(
        `${API}/api/ross1000/group-credentials/${cred.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Credenziali ${cred.province} salvate`);
      setEditDrafts((prev) => {
        const clone = { ...prev };
        delete clone[cred.id];
        return clone;
      });
      await fetchCredentials();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(null);
    }
  };

  const createCredential = async () => {
    if (!newEntry.region || !newEntry.province || !newEntry.province_code) {
      toast.error('Regione, Provincia e Codice Provincia sono obbligatori');
      return;
    }
    try {
      setSaving('new');
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/ross1000/group-credentials`, newEntry, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Account di gruppo creato');
      setShowAddDialog(false);
      setNewEntry({
        region: '',
        province: '',
        province_code: '',
        username: '',
        password: '',
      });
      await fetchCredentials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(null);
    }
  };

  const deleteCredential = async () => {
    if (!deleteConfirm) return;
    try {
      setSaving(deleteConfirm.id);
      const token = localStorage.getItem('hw_token');
      await axios.delete(
        `${API}/api/ross1000/group-credentials/${deleteConfirm.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Account di gruppo eliminato');
      setDeleteConfirm(null);
      await fetchCredentials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setSaving(null);
    }
  };

  // Group by region
  const byRegion = credentials.reduce((acc, c) => {
    if (!acc[c.region]) acc[c.region] = [];
    acc[c.region].push(c);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ross1000-group-credentials-manager">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              Account di Gruppo ROSS1000 / ISTAT
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Credenziali condivise a livello provinciale. Gli immobili con{' '}
              <strong>mode = group</strong> usano automaticamente queste credenziali per l'invio dei flussi
              turistici.
            </p>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            data-testid="add-group-cred-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Aggiungi Provincia
          </Button>
        </CardHeader>
      </Card>

      {Object.entries(byRegion).length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            Nessun account di gruppo configurato. Aggiungi la prima provincia per iniziare.
          </CardContent>
        </Card>
      )}

      {Object.entries(byRegion).map(([region, items]) => (
        <Card key={region} data-testid={`region-block-${region}`}>
          <CardHeader
            className="cursor-pointer select-none"
            onClick={() => setExpanded((prev) => ({ ...prev, [region]: !prev[region] }))}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                {expanded[region] ? (
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-500" />
                )}
                🗺️ {region}
                <span className="text-xs font-normal text-slate-500">
                  {items.length} provinc{items.length === 1 ? 'ia' : 'e'}
                </span>
              </CardTitle>
            </div>
          </CardHeader>
          {expanded[region] && (
            <CardContent className="pt-0">
              <div className="space-y-3">
                {items.map((cred) => {
                  const pwdVisible = !!showPwd[cred.id];
                  const dirty = isDirty(cred);
                  const isSaving = saving === cred.id;
                  return (
                    <div
                      key={cred.id}
                      className="border rounded-lg p-3 bg-slate-50 hover:bg-white transition"
                      data-testid={`group-cred-row-${cred.province_code}`}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_1fr_auto] gap-3 items-center">
                        <div>
                          <div className="font-semibold text-slate-800">
                            {cred.province}
                          </div>
                          <div className="text-xs text-slate-500">
                            Cod. {cred.province_code}
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Username</Label>
                          <Input
                            value={getFieldValue(cred, 'username')}
                            onChange={(e) =>
                              handleDraftChange(cred.id, 'username', e.target.value)
                            }
                            placeholder="Username portale"
                            data-testid={`group-cred-username-${cred.province_code}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Password</Label>
                          <div className="relative">
                            <Input
                              type={pwdVisible ? 'text' : 'password'}
                              value={getFieldValue(cred, 'password')}
                              onChange={(e) =>
                                handleDraftChange(cred.id, 'password', e.target.value)
                              }
                              placeholder="Password"
                              className="pr-10"
                              data-testid={`group-cred-password-${cred.province_code}`}
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              onClick={() =>
                                setShowPwd((prev) => ({ ...prev, [cred.id]: !prev[cred.id] }))
                              }
                            >
                              {pwdVisible ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant={dirty ? 'default' : 'outline'}
                            size="sm"
                            disabled={!dirty || isSaving}
                            onClick={() => saveCredential(cred)}
                            data-testid={`group-cred-save-${cred.province_code}`}
                          >
                            {isSaving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirm(cred)}
                            data-testid={`group-cred-delete-${cred.province_code}`}
                          >
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      ))}

      {/* Add dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Account di Gruppo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Regione *</Label>
              <Input
                value={newEntry.region}
                onChange={(e) => setNewEntry({ ...newEntry, region: e.target.value })}
                placeholder="Es. Liguria"
                data-testid="add-region-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Provincia *</Label>
                <Input
                  value={newEntry.province}
                  onChange={(e) => setNewEntry({ ...newEntry, province: e.target.value })}
                  placeholder="Es. Genova"
                  data-testid="add-province-input"
                />
              </div>
              <div>
                <Label>Codice Provincia *</Label>
                <Input
                  value={newEntry.province_code}
                  onChange={(e) =>
                    setNewEntry({ ...newEntry, province_code: e.target.value.toUpperCase() })
                  }
                  placeholder="GE"
                  maxLength={2}
                  className="uppercase"
                  data-testid="add-province-code-input"
                />
              </div>
            </div>
            <div>
              <Label>Username</Label>
              <Input
                value={newEntry.username}
                onChange={(e) => setNewEntry({ ...newEntry, username: e.target.value })}
                placeholder="Username portale"
                data-testid="add-username-input"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={newEntry.password}
                onChange={(e) => setNewEntry({ ...newEntry, password: e.target.value })}
                placeholder="Password"
                data-testid="add-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annulla
            </Button>
            <Button
              onClick={createCredential}
              disabled={saving === 'new'}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-group-cred-confirm"
            >
              {saving === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma eliminazione</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Eliminare l'account di gruppo per{' '}
            <strong>
              {deleteConfirm?.province} ({deleteConfirm?.region})
            </strong>
            ? Gli immobili che usano questo account non avranno più credenziali automatiche.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={deleteCredential}
              disabled={saving === deleteConfirm?.id}
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ross1000GroupCredentialsManager;
